import React from 'react';
import { HiOutlineReceiptRefund } from 'react-icons/hi';
import PurchaseCustomModal from '../../Elements/PurchaseCustomModal';
import { formatApiDateTime } from '../../../lib/helpers';

function TransactionDetailModal({ active, onClose, transaction, onReverse }) {
  if (!transaction) return null;

  const isVenta = ['purchase', 'sale', 'venta'].includes(transaction.type?.toLowerCase());

  const formatDate = (dateString) =>
    formatApiDateTime(dateString, { fallback: '-', includeSeconds: true });

  const getTypeColor = (type) => {
    const t = type?.toLowerCase();
    if (t === 'purchase' || t === 'sale' || t === 'venta') return 'bg-green-100 text-green-800 border-green-300';
    if (t === 'credit' || t === 'credito' || t === 'crédito' || t === 'add_cash') return 'bg-purple-100 text-purple-800 border-purple-300';
    if (t === 'refund' || t === 'reembolso') return 'bg-red-100 text-red-800 border-red-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getTypeLabel = (type) => {
    const t = type?.toLowerCase();
    if (t === 'purchase' || t === 'sale' || t === 'venta') return 'Venta';
    if (t === 'credit' || t === 'credito' || t === 'crédito' || t === 'add_cash') return 'Crédito';
    if (t === 'refund' || t === 'reembolso') return 'Reembolso';
    return type || 'Desconocido';
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    if (s === 'completed' || s === 'completado' || s === 'success') return 'bg-green-100 text-green-800';
    if (s === 'pending' || s === 'pendiente') return 'bg-yellow-100 text-yellow-800';
    if (s === 'failed' || s === 'fallido' || s === 'cancelled' || s === 'cancelado') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const items = transaction.items || [];
  const totalItems = items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
  const calculatedTotal = items.reduce((sum, item) => {
    const quantity = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.unit_price) || 0;
    return sum + (quantity * price);
  }, 0);

  const modalContent = (
    <div className="space-y-6">
      {/* Header con ID y Tipo */}
      <div className="flex items-center justify-between pb-4 border-b">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Transacción #{transaction.id?.toString().slice(0, 8) || 'N/A'}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {formatDate(transaction.timestamp || transaction.created_at)}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`px-3 py-1 text-sm font-semibold rounded-full border ${getTypeColor(transaction.type)}`}>
            {getTypeLabel(transaction.type)}
          </span>
          {transaction.status && (
            <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(transaction.status)}`}>
              {transaction.status}
            </span>
          )}
        </div>
      </div>

      {/* Información del Usuario */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Información del Cliente</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Nombre</p>
            <p className="text-base font-medium text-gray-900">
              {transaction.user_name || 'No disponible'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Email</p>
            <p className="text-base font-medium text-gray-900">
              {transaction.user_email || 'No disponible'}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-500">ID de Usuario</p>
            <p className="text-base font-mono text-gray-700">
              {transaction.user_id?.toString().slice(0, 8) || 'N/A'}
            </p>
          </div>
          {transaction.processed_by_name && (
            <div>
              <p className="text-sm text-gray-500">Atendido por</p>
              <p className="text-base font-medium text-gray-900">
                {transaction.processed_by_name}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Resumen de Montos */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Resumen Financiero</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-600">Total de la Transacción</p>
            <p className="text-2xl font-bold text-blue-900">
              €{parseFloat(transaction.amount || 0).toFixed(2)}
            </p>
          </div>
          {items.length > 0 && (
            <>
              <div>
                <p className="text-sm text-gray-600">Total Calculado</p>
                <p className="text-xl font-semibold text-gray-800">
                  €{calculatedTotal.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total de Items</p>
                <p className="text-xl font-semibold text-gray-800">
                  {totalItems} {totalItems === 1 ? 'unidad' : 'unidades'}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Items de la Transacción */}
      {items.length > 0 ? (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Productos ({items.length})
          </h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-700">Producto</th>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-700 text-right">Cantidad</th>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-700 text-right">Precio Unit.</th>
                    <th className="px-4 py-3 text-sm font-semibold text-gray-700 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {items.map((item, index) => {
                    const quantity = parseFloat(item.quantity) || 0;
                    const price = parseFloat(item.unit_price) || 0;
                    const subtotal = quantity * price;
                    
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">
                            {item.product_name || `Producto #${item.product_id?.toString().slice(0, 8) || 'N/A'}`}
                          </div>
                          {item.product_id && (
                            <div className="text-xs text-gray-500 font-mono">
                              ID: {item.product_id.toString().slice(0, 8)}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">
                          {quantity}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700">
                          €{price.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">
                          €{subtotal.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td colSpan="3" className="px-4 py-3 text-right font-semibold text-gray-900">
                      Total:
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-lg text-blue-900">
                      €{calculatedTotal.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            <strong>Nota:</strong> Esta transacción no tiene items asociados. 
            {transaction.type?.toLowerCase() === 'add_cash' && ' Es una operación de añadir crédito.'}
          </p>
        </div>
      )}

      {/* Información Adicional */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Información Adicional</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">ID de Transacción</p>
            <p className="font-mono text-gray-900 mt-1">{transaction.id?.toString() || 'N/A'}</p>
          </div>
          {transaction.venue_id && (
            <div>
              <p className="text-gray-500">ID de Tienda</p>
              <p className="font-mono text-gray-900 mt-1">{transaction.venue_id.toString().slice(0, 8) || 'N/A'}</p>
            </div>
          )}
          <div>
            <p className="text-gray-500">Tipo</p>
            <p className="text-gray-900 mt-1">{getTypeLabel(transaction.type)}</p>
          </div>
          {transaction.status && (
            <div>
              <p className="text-gray-500">Estado</p>
              <p className="text-gray-900 mt-1">{transaction.status}</p>
            </div>
          )}
        </div>
      </div>

      {/* Acciones */}
      <div className="flex justify-between items-center pt-4 border-t">
        <div>
          {isVenta && onReverse && (
            <button
              onClick={() => { onReverse(transaction); onClose(); }}
              className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 transition-colors"
              title="Anular venta: devuelve el stock y, si fue a crédito, el saldo al socio"
            >
              <HiOutlineReceiptRefund className="w-5 h-5" /> Devolver
            </button>
          )}
        </div>
        <button
          onClick={onClose}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          Cerrar
        </button>
      </div>
    </div>
  );

  return (
    <PurchaseCustomModal
      active={active}
      handleModal={onClose}
      title="Detalles de la Transacción"
    >
      {modalContent}
    </PurchaseCustomModal>
  );
}

export default TransactionDetailModal;
